import { isRichTextEmpty } from './rich-text-utils';

export type FieldKind = 'text' | 'multiline' | 'richText' | 'image' | 'url' | 'youtube' | 'icon' | 'number' | 'checkbox' | 'date' | 'file';

export interface FieldSpec {
    label: string;
    kind: FieldKind;
    required?: boolean;
    // Mensagem de obrigatoriedade; padrão "<label> é obrigatório"
    requiredMessage?: string;
    helper?: string;
    // Largura em telas médias (colunas do Grid, padrão 12)
    md?: number;
    min?: number;
    max?: number;
    rangeMessage?: string;
}

export type FieldMap<K extends string> = Partial<Record<K, FieldSpec>>;
export type FieldErrors = Record<string, string>;

const YOUTUBE_REGEX = /^(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtube-nocookie\.com\/embed\/|youtu\.be\/)[A-Za-z0-9_-]{11}/i;

export function isYoutubeUrl(value?: string): boolean {
    return !!value && YOUTUBE_REGEX.test(value.trim());
}

function isEmpty(spec: FieldSpec, value: any): boolean {
    if (value === undefined || value === null) return true;
    if (typeof value === 'string') {
        if (spec.kind === 'richText') return isRichTextEmpty(value);
        return value.trim() === '';
    }
    return false;
}

export function validateFields<K extends string>(fields: FieldMap<K>, value: Record<string, any>): FieldErrors {
    const errors: FieldErrors = {};
    (Object.keys(fields) as K[]).forEach((key) => {
        const spec = fields[key];
        if (!spec) return;
        const fieldValue = value[key];
        const empty = isEmpty(spec, fieldValue);

        if (spec.required && empty) {
            errors[key] = spec.requiredMessage || `${spec.label} é obrigatório`;
            return;
        }
        if (empty) return;

        if (spec.kind === 'youtube' && !isYoutubeUrl(fieldValue)) {
            errors[key] = 'Informe uma URL válida do YouTube.';
        } else if (spec.kind === 'number') {
            const n = Number(fieldValue);
            if (Number.isNaN(n) || (spec.min !== undefined && n < spec.min) || (spec.max !== undefined && n > spec.max)) {
                errors[key] = spec.rangeMessage || `${spec.label} inválido`;
            }
        }
    });
    return errors;
}

// Mantém apenas os campos configurados para o tipo, limpando o que a API ignoraria
export function pickFields<K extends string, T extends Partial<Record<K, any>>>(fields: FieldMap<K>, value: T): Partial<T> {
    const result: Partial<T> = {};
    (Object.keys(fields) as K[]).forEach((key) => {
        const v = (value as any)[key];
        (result as any)[key] = typeof v === 'string' ? (v.trim() === '' ? undefined : v) : v;
        // Arquivo: o nome original e o tamanho acompanham o caminho (ex.: file, fileOriginalName, fileSize)
        if (fields[key]?.kind === 'file') {
            (result as any)[`${key}OriginalName`] = v ? (value as any)[`${key}OriginalName`] : undefined;
            (result as any)[`${key}Size`] = v ? (value as any)[`${key}Size`] : undefined;
        }
    });
    return result;
}

// Separa os campos de um tipo em grupos do drawer (ex.: identificação x conteúdo)
export function pickFieldGroup<K extends string>(fields: FieldMap<K>, keys: K[], include: boolean): FieldMap<K> {
    const result: FieldMap<K> = {};
    (Object.keys(fields) as K[])
        .filter((key) => keys.includes(key) === include)
        .forEach((key) => { result[key] = fields[key]; });
    return result;
}

// Request de seção (home e páginas): campos do tipo + configuração reenviada, pois não é editável na tela
export function buildSectionRequest<K extends string>(fields: FieldMap<K>, value: Record<string, any>) {
    return {
        ...pickFields(fields, value),
        configuration: value.configuration as string | undefined,
        isVisible: value.isVisible !== false,
    };
}
